import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { IntlShape, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  PROMO_RESULTS_ORDERS_PER_PAGE,
  PromoResultsDateFilters,
  PromoResultsDetail,
  PromoResultsRow,
} from '../models/ScheduledOfferResultsModel';
import { getScheduledOfferResults } from '../services/ScheduledOfferCRUD';

/** Decimal string from the API rendered as money, e.g. "1.234,50 €". */
export function formatAmount(
  intl: IntlShape,
  value: string,
  currencySymbol: string
): string {
  const amount = intl.formatNumber(Number(value), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${amount} ${currencySymbol}`;
}

/** Date as dd/mm/yyyy, same formatting as the B2B lead orders list. */
export function formatShortDate(intl: IntlShape, isoDate: string): string {
  return intl.formatDate(new Date(isoDate), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

interface ScheduledOfferOrdersModalProps {
  offer: PromoResultsRow | null;
  dateFilters: PromoResultsDateFilters;
  onClose: () => void;
}

export default function ScheduledOfferOrdersModal({
  offer,
  dateFilters,
  onClose,
}: ScheduledOfferOrdersModalProps) {
  const intl = useIntl();
  const [detail, setDetail] = useState<PromoResultsDetail | null>(null);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);

  async function fetchDetail(offerId: number, pageToLoad: number) {
    setLoading(true);
    try {
      const { data } = await getScheduledOfferResults(
        offerId,
        dateFilters,
        pageToLoad
      );
      setDetail(data);
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'promoResults.error' }));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setDetail(null);
    setPage(1);
    if (offer) fetchDetail(offer.id, 1);
    // Reload only when another offer is opened; the page changes below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer?.id]);

  function goToPage(nextPage: number) {
    if (!offer) return;
    setPage(nextPage);
    fetchDetail(offer.id, nextPage);
  }

  const orders = detail?.order_list.results ?? [];
  const totalPages = detail
    ? Math.max(
        1,
        Math.ceil(detail.order_list.count / PROMO_RESULTS_ORDERS_PER_PAGE)
      )
    : 1;
  const currencySymbol = offer?.currency_symbol ?? '€';

  return (
    <Modal show={offer !== null} onHide={onClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {intl.formatMessage(
            { id: 'promoResults.ordersTitle' },
            { name: offer?.name ?? '' }
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div
            className="spinner-border spinner-border-sm text-primary"
            role="status"
          />
        )}
        {!loading && detail && orders.length === 0 && (
          <p>{intl.formatMessage({ id: 'promoResults.noOrders' })}</p>
        )}
        {!loading && orders.length > 0 && (
          <div className="table-responsive">
            <table className="table table-striped align-middle">
              <thead>
                <tr className="fw-bolder">
                  <th>
                    {intl.formatMessage({ id: 'promoResults.orderCode' })}
                  </th>
                  <th>
                    {intl.formatMessage({ id: 'promoResults.chargeDate' })}
                  </th>
                  <th>
                    {intl.formatMessage({ id: 'promoResults.orderState' })}
                  </th>
                  <th>{intl.formatMessage({ id: 'promoResults.customer' })}</th>
                  <th>{intl.formatMessage({ id: 'promoResults.market' })}</th>
                  <th className="text-end">
                    {intl.formatMessage({ id: 'promoResults.grossRevenue' })}
                  </th>
                  <th className="text-end">
                    {intl.formatMessage({ id: 'promoResults.netRevenue' })}
                  </th>
                  <th className="text-end">
                    {intl.formatMessage({ id: 'promoResults.discount' })}
                  </th>
                  <th>
                    {intl.formatMessage({ id: 'promoResults.otherPromos' })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td>{order.code}</td>
                    <td>
                      {order.charge_date
                        ? formatShortDate(intl, order.charge_date)
                        : '—'}
                    </td>
                    <td>{order.state}</td>
                    <td>
                      <Link to={`/user/${order.user}/orders`} onClick={onClose}>
                        {order.user_email}
                      </Link>
                    </td>
                    <td>{order.country}</td>
                    <td className="text-end">
                      {formatAmount(intl, order.gross_revenue, currencySymbol)}
                    </td>
                    <td className="text-end">
                      {formatAmount(intl, order.net_revenue, currencySymbol)}
                    </td>
                    <td className="text-end">
                      {formatAmount(
                        intl,
                        order.discount_inc_tax,
                        currencySymbol
                      )}
                    </td>
                    <td>
                      {order.other_promos.length === 0
                        ? '—'
                        : order.other_promos.map((promo) => (
                            <span
                              key={promo.id}
                              className="badge badge-light-warning me-1"
                            >
                              {promo.name ?? `#${promo.id}`}
                            </span>
                          ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <span className="text-muted">
          {detail &&
            intl.formatMessage(
              { id: 'promoResults.ordersCount' },
              { count: detail.order_list.count }
            )}
        </span>
        <div className="d-flex gap-2 align-items-center">
          <button
            className="btn btn-light btn-sm"
            disabled={loading || !detail?.order_list.previous}
            onClick={() => goToPage(page - 1)}
          >
            <span className="fa fa-chevron-left"></span>
          </button>
          <span>
            {page} / {totalPages}
          </span>
          <button
            className="btn btn-light btn-sm"
            disabled={loading || !detail?.order_list.next}
            onClick={() => goToPage(page + 1)}
          >
            <span className="fa fa-chevron-right"></span>
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
